import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import IndiaMap from '../components/IndiaMap';
import { Award, MapPin, Users, TrendingUp, CheckCircle2, ArrowRight, Target, Eye, Compass, BadgeCheck } from 'lucide-react';
import { stats, states } from '../data/mock';

export default function About() {
  return (
    <div>
      <PageHero
        title="Three decades of quiet, dependable excellence."
        subtitle="From a Mangalore canteen in 1994 to a Pvt. Ltd. incorporated in 2007 — three decades of trust compounded year after year."
        image="/assets/food-beverage.png"
        breadcrumbs={[{ label: 'About' }]}
      />

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="grid grid-cols-5 grid-rows-6 gap-3">
              <div className="col-span-3 row-span-6 rounded-2xl overflow-hidden shadow-xl bg-slate-100 aspect-[3/4]">
                <img
                  src="/assets/front-office.png"
                  alt="Durga front office & reception team"
                  className="w-full h-full"
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-lg bg-slate-100">
                <img
                  src="/assets/employee-recognition.png"
                  alt="Employee recognition programme"
                  className="w-full h-full"
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="/assets/hk-lobby.webp"
                  alt="Durga housekeeping team on-site"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-8 bg-[#0A2540] text-white p-6 rounded-2xl shadow-2xl w-56">
              <Award className="mb-2" size={26} />
              <div className="text-sm font-semibold leading-snug">India 500 Best MSME Award 2024</div>
            </div>
          </div>

          <div>
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Our Story</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-6">
              A journey from a single kitchen to a national workforce.
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed text-[15px]">
              <p>
                Durga Management Service was started in <strong className="text-slate-900">1994</strong> by Mr. B.S. Shetty as <em>Sri Durga Catering Services</em> – a small canteen operation with a big commitment to quality.
              </p>
              <p>
                In <strong className="text-slate-900">2003</strong>, we entered hospitality management as <em>Sri Durga Hospitality Services</em>. In <strong className="text-slate-900">2007</strong>, the company took its present name – <em>Durga Facility Management Services Pvt. Ltd.</em> – and onboarded two directors bringing deep facility-management expertise.
              </p>
              <p>
                Today, we run mechanized housekeeping, security, pest control, front-office, and on-demand operations across Mumbai, Bangalore, Mangaluru and beyond &mdash; <strong className="text-slate-900">30+ years</strong> on, with the same discipline that started it all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { Icon: Target, title: 'Our Mission', desc: 'To deliver world-class, measurable facility outcomes that free our clients to focus on their core business.' },
              { Icon: Eye, title: 'Our Vision', desc: 'To be India’s most trusted, technology-forward integrated facility management partner.' },
              { Icon: Compass, title: 'Our Values', desc: 'Perfection, precision and plurality – backed by integrity, safety and sustainability in every engagement.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="bg-white p-8 rounded-2xl border border-slate-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-slate-100 text-[#0A2540] flex items-center justify-center mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-600 leading-relaxed text-[15px]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MD Message */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Message from the MD</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-6">
              &ldquo;Our commitment to desired results is the reason clients stay for decades.&rdquo;
            </h2>
            <p className="text-slate-600 leading-relaxed text-[15px] italic border-l-4 border-[#F5B800] pl-5 mb-6">
              Durga Facility Management is a group of dedicated professionals serving the needs of our customers through efficient, reliable and economic facility management services. The continuous efforts of our team is the leading force behind our success story. We remain committed to deliver desired results to our valued customers throughout the service period.
            </p>
            <div>
              <div className="font-bold text-slate-900 text-lg">Mr. B S Shetty</div>
              <div className="text-sm text-[#0A2540] font-medium">Managing Director &amp; Founder</div>
            </div>
          </div>
          <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-xl aspect-[4/3] bg-slate-100">
            <img
              src="/assets/md-shetty.jpg"
              alt="Mr. B S Shetty, Managing Director"
              className="w-full h-full"
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>
      </section>

      {/* Award Moment */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl border border-slate-100 aspect-[4/3]">
              <img
                src="/assets/india500-award-2024.jpg"
                alt="Durga Facility Management receiving the India 5000 Best MSME Award 2024"
                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 md:-right-8 bg-[#F5B800] text-[#0A2540] px-5 py-3 rounded-xl shadow-lg font-bold text-sm tracking-tight hidden md:block">
              WINNER · 2024
            </div>
          </div>

          <div>
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Recognition</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05] tracking-tight mb-6">
              India 5000 Best MSME Award <span className="text-[#0A2540] relative inline-block">2024
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#F5B800]/40 -z-10 rounded-sm" aria-hidden="true" />
              </span>.
            </h2>
            <p className="text-slate-600 leading-relaxed text-[15px] mb-4">
              At the India 5000 MSME Conference 2024, Durga Facility Management Services Pvt. Ltd. was honoured as one of the Best MSMEs in India — a national recognition of quality, reliability and dedicated service delivery across three decades.
            </p>
            <p className="text-slate-600 leading-relaxed text-[15px] mb-6">
              We share this award with every member of our team, our partners and the clients who trust us to run their facilities every single day.
            </p>

            <div className="flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-1.5 text-xs font-semibold text-slate-700">
                <BadgeCheck size={14} className="text-[#F5B800]" /> India 5000 Best MSME
              </div>
              <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-1.5 text-xs font-semibold text-slate-700">
                <Award size={14} className="text-[#F5B800]" /> Winner · 2024
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Presence */}
      <section className="py-24 bg-slate-50 border-y border-slate-100 relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pb-14 mb-14 border-b border-slate-200">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-extrabold text-[#0A2540] tracking-tight">{s.num}</div>
                <div className="text-xs md:text-sm text-slate-500 mt-2 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
            <div>
              <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Our Presence</div>
              <h2 className="text-3xl md:text-4xl font-bold leading-[1.1] tracking-tight mb-5 text-slate-900">Five states. One consistent standard.</h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Durga Facility Management has marked its presence across Karnataka, Goa, Maharashtra, Tamil Nadu and Telangana, with regional offices in Mangalore, Bengaluru and Mumbai. 
              </p>

              <ul className="space-y-3 mb-8">
                {['Background verification for every hire', 'Regular training in equipment & safety', 'Statutory-compliant payroll & PF/ESI', 'Employee recognition & reward programs'].map((p) => (
                  <li key={p} className="flex items-center gap-3 text-slate-700 text-sm"><CheckCircle2 size={16} className="text-[#F5B800] shrink-0" /> {p}</li>
                ))}
              </ul>

              <Link to="/contact" className="btn-cta inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-[14px]">
                Partner with Durga <ArrowRight size={16} />
              </Link>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8 shadow-sm">
              <div className="text-center mb-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A2540]">Where we operate</div>
                <div className="text-sm text-slate-500 mt-1">Hover over a state to see our regional hub</div>
              </div>
              <IndiaMap states={states} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
